import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { boampSearchService } from '../services/boamp-search.service';
import {
  BoampSearchParams,
  ImportItem,
} from '../types/opportunity.types';

export const BOAMP_SEARCH_KEY = 'boamp-search';
const TED_SEARCH_KEY = 'ted-search';
const EXCLUDED_KEY = 'excluded-opportunities';

/**
 * Hook pour la recherche BOAMP
 */
export function useBoampSearch(params: BoampSearchParams, enabled = true) {
  return useQuery({
    queryKey: [BOAMP_SEARCH_KEY, params],
    queryFn: () => boampSearchService.search(params),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook pour importer des opportunités
 */
export function useImportOpportunities() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (items: ImportItem[]) =>
      boampSearchService.importOpportunities(items),
    onSuccess: () => {
      // Invalider les recherches pour rafraîchir les flags isImported
      queryClient.invalidateQueries({ queryKey: [BOAMP_SEARCH_KEY] });
      queryClient.invalidateQueries({ queryKey: [TED_SEARCH_KEY] });
    },
  });
}

/**
 * Hook pour exclure une opportunité
 */
export function useExcludeOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      externalId,
      source,
      reason,
    }: {
      externalId: string;
      source: 'boamp' | 'ted';
      reason?: string;
    }) => boampSearchService.excludeOpportunity(externalId, source, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOAMP_SEARCH_KEY] });
      queryClient.invalidateQueries({ queryKey: [TED_SEARCH_KEY] });
      queryClient.invalidateQueries({ queryKey: [EXCLUDED_KEY] });
    },
  });
}

/**
 * Hook pour réintégrer une opportunité exclue
 */
export function useRemoveExclusion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      externalId,
      source,
    }: {
      externalId: string;
      source: 'boamp' | 'ted';
    }) => boampSearchService.removeExclusion(externalId, source),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOAMP_SEARCH_KEY] });
      queryClient.invalidateQueries({ queryKey: [TED_SEARCH_KEY] });
      queryClient.invalidateQueries({ queryKey: [EXCLUDED_KEY] });
    },
  });
}

/**
 * Hook pour lister les opportunités exclues
 */
export function useExcludedOpportunities(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: [EXCLUDED_KEY, page, pageSize],
    queryFn: () => boampSearchService.getExcludedOpportunities(page, pageSize),
  });
}

/**
 * Hook pour vérifier des opportunités
 */
export function useCheckOpportunities(
  externalIds: string[],
  source: 'boamp' | 'ted',
  enabled = true
) {
  return useQuery({
    queryKey: ['check-opportunities', externalIds, source],
    queryFn: () => boampSearchService.checkOpportunities(externalIds, source),
    enabled: enabled && externalIds.length > 0,
  });
}
